import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { BasicTitle, BiggerSubtitle } from '@genially/design-system';

const slideInFwd = keyframes({
  '0%': {
    transform: 'rotate(-540deg) scale(0)',
    opacity: 0,
  },
  '100%': {
    transform: 'rotate(0) scale(1)',
    opacity: 1,
  },
});

const slideInFwdCenter = keyframes({
  '0%': {
    transform: 'scale(0)',
    opacity: 0,
  },
  '100%': {
    transform: 'scale(1)',
    opacity: 1,
  },
});

const slideInTop = keyframes({
  '0%': {
    transform: 'translateY(-100%)',
    opacity: 0,
  },
  '100%': {
    transform: 'translateY(0)',
    opacity: 1,
  },
});

const starfieldRotate = keyframes({
  from: {
    transform: 'rotate(0deg)',
  },
  to: {
    transform: 'rotate(360deg)',
  },
});

const scaleIn = keyframes({
  '0%': {
    transform: 'scale(0)',
    opacity: 0,
  },
  '100%': {
    transform: 'scale(1)',
    opacity: 1,
  },
});

const fadeOut = keyframes({
  '0%': {
    opacity: 1,
  },
  '100%': {
    opacity: 0,
  },
});

const withAnimationImage = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${slideInFwd} 0.375s ease-out both;
        animation-delay: 0.375s;
      `,
  );

const withAnimationTitle = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${slideInFwdCenter} 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        animation-delay: 0.75s;
      `,
  );

const withAnimationSubtitle = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${slideInTop} 0.3125s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        animation-delay: 1s;
      `,
  );

const withAnimationStarFieldWrapper = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    ({ $activeAnimation }) =>
      $activeAnimation &&
      css`
        animation: ${scaleIn} 0.625s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
      `,
  );

const withAnimationStarFieldChild = (Component: ElementType) =>
  styled(Component)<{ $activeAnimation: boolean }>(
    () => css`
      animation-iteration-count: infinite;
      animation-timing-function: linear;
      animation-name: ${starfieldRotate};
    `,
  );

const withAnimationWrapper = (Component: ElementType) =>
  styled(Component)<{ $activeHideAnimation: boolean }>(
    ({ $activeHideAnimation }) =>
      $activeHideAnimation &&
      css`
        animation: ${fadeOut} 0.625s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
      `,
  );

export const StyledImage = withAnimationImage(
  styled.img(({ theme: { size } }) => ({
    width: size.units(22.5),
  })),
);

export const StyledBasicTitle = withAnimationTitle(
  styled(BasicTitle)(({ theme: { color } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
  })),
);

export const StyledBiggerSubtitle = withAnimationSubtitle(
  styled(BiggerSubtitle)(({ theme: { color, size } }) => ({
    color: color.content.reversed.default(),
    textAlign: 'center',
    padding: size.units(1.5),
  })),
);

export const Wrapper = withAnimationWrapper(
  styled.div(({ theme: { size } }) => ({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    [StyledBasicTitle]: {
      marginTop: size.units(3),
    },

    [StyledBiggerSubtitle]: {
      marginTop: size.units(1.5),
    },
  })),
);

export const StarFieldWrapper = withAnimationStarFieldWrapper(
  styled.div(() => ({
    display: 'block',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    position: 'absolute' as const,
  })),
);

export const StarFieldChild = withAnimationStarFieldChild(
  styled.span(() => ({
    position: 'absolute' as const,
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    backgroundSize: '200px 200px',
    backgroundImage:
      'radial-gradient(3px 3px at 40px 60px,#ccc,rgba(0, 0, 0, 0)), radial-gradient(2px 2px at 20px 50px, #ddd, rgba(0, 0, 0, 0)),radial-gradient(2px 2px at 30px 100px, #eee, rgba(0, 0, 0, 0)),radial-gradient(2px 2px at 40px 60px, #fff, rgba(0, 0, 0, 0)),radial-gradient(2px 2px at 110px 90px, #ccc, rgba(0, 0, 0, 0)),radial-gradient(2px 2px at 190px 150px, #ddd, rgba(0, 0, 0, 0)),radial-gradient(2px 2px at 90px 123px, #ddd, rgba(0, 0, 0, 0)),radial-gradient(2px 2px at 25px 200px, #ddd, rgba(0, 0, 0, 0))',
    backgroundRepeat: 'repeat',
    transformOrigin: '50% 50%',

    '&:nth-child(1)': {
      animationDuration: '15s',
    },
    '&:nth-child(2)': {
      animationDuration: '18s',
    },
    '&:nth-child(3)': {
      animationDuration: '22.5s',
    },
    '&:nth-child(4)': {
      animationDuration: '31.5s',
    },
    '&:nth-child(5)': {
      animationDuration: '36s',
    },
    '&:nth-child(6)': {
      animationDuration: '43.5s',
    },
    '&:nth-child(7)': {
      animationDuration: '48s',
    },
    '&:nth-child(8)': {
      animationDuration: '54s',
    },
  })),
);
